import { Box, Divider, ListItemIcon, ListItemText, MenuItem } from "@mui/material"
import Grid from "@mui/material/Grid"
import Menu from "@mui/material/Menu"
import { MouseEventHandler, useRef, useState } from "react"
import { useAppSelector } from "../../../hooks/useRedux"

interface Item {
    onClick: MouseEventHandler<HTMLLIElement>
    shortCut?: string
    icon: any
    title: string
    disabled?: boolean
    divider?: boolean
    color?: "error" | "warning" | "success" | "info"
}

export type MenuItemT = {
    onClick: MouseEventHandler<HTMLLIElement>
    icon: any
    title: string
    shortCut?: string
    disabled?: boolean
    divider?: boolean
    submenu?: MenuItemT[]
    hidden?: boolean
    color?: "error" | "warning" | "success" | "info"
}

interface Props {
    /**
     * Pass a FUNCTION whenever building the items is not free.
     *
     * The items are only needed once the menu is actually open, but as a plain array they
     * were rebuilt on every render of the owner — and the owners are things like TimeStamp
     * (~500 instances, ~12 items each, every one holding an MUI SVG icon element) and
     * CanvasItem (~200 instances). That is tens of thousands of React elements created per
     * render of the timeline or the canvas, for a menu almost nobody has open.
     */
    data: Item[] | (() => MenuItemT[])
    scaleCoef?: number
    menuLabel?: JSX.Element
    /**
     * Fired before the menu opens, so the owner can make the menu's target the current
     * one — a right click on something that is not selected should act on THAT thing.
     *
     * It has to be a callback rather than an `onContextMenu` on a wrapper of this
     * component: the handler below stops the event, so nothing outside would ever see it.
     */
    onBeforeOpen?: () => void
}

const NO_ITEMS: MenuItemT[] = []

const getValueWithoutUnit = (value: string): number => Number(value.replace(/[^\d.-]/g, ""))

const RightClickContextMenu = ({ data, scaleCoef, menuLabel, onBeforeOpen }: Props) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const [mouseClickPosition, setMouseClickPosition] = useState<{ x: number; y: number }>({
        x: 0,
        y: 0,
    })
    const canvasZoom = useAppSelector((state) => state.editor.value.settings.canvasZoom)
    const canvasScale = canvasZoom / 100

    const handleClick = (event: any) => {
        event.preventDefault()
        if (!event) event = window.event
        event.cancelBubble = true
        if (event.stopPropagation) event.stopPropagation()

        onBeforeOpen?.()

        const left =
            scaleCoef !== undefined ? getValueWithoutUnit(event.target.style.left) * scaleCoef : 0 // for absolutely positioned, scaled elements in the Canvas
        const top =
            scaleCoef !== undefined ? getValueWithoutUnit(event.target.style.top) * scaleCoef : 0 // for absolutely positioned, scaled elements in the Canvas

        setMouseClickPosition({
            x: event.clientX - event.target.getBoundingClientRect().left + left,
            y: event.clientY - event.target.getBoundingClientRect().top + top,
        })

        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => setAnchorEl(null)

    const open = Boolean(anchorEl)

    // Resolved only while open, and kept afterwards so the closing transition still has
    // something to show.
    const itemsRef = useRef<MenuItemT[]>(NO_ITEMS)
    if (open) {
        itemsRef.current = typeof data === "function" ? data() : data
    }
    const items = itemsRef.current

    return (
        <>
            <Grid onContextMenu={handleClick}>{menuLabel}</Grid>
            <Menu
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: mouseClickPosition.y * canvasScale,
                    horizontal: mouseClickPosition.x * canvasScale,
                }}
                onClick={handleClose}
            >
                {items.map((x: MenuItemT, index: number) =>
                    x.hidden ? null : (
                        <div key={index}>
                            <MenuItem
                                key={index}
                                onClick={(event) => {
                                    x.onClick(event)
                                    handleClose()
                                }}
                                disabled={x.disabled}
                            >
                                <ListItemIcon
                                    sx={x.color ? { color: `${x.color}.main` } : undefined}
                                >
                                    {x.icon}
                                </ListItemIcon>
                                <ListItemText
                                    sx={x.color ? { color: `${x.color}.main` } : undefined}
                                >
                                    {x.title}
                                </ListItemText>
                                <Box
                                    component="span"
                                    sx={{
                                        paddingLeft: x.shortCut ? "6px" : 0,
                                        color: x.color ? `${x.color}.main` : undefined,
                                    }}
                                >
                                    {x.shortCut}
                                </Box>
                            </MenuItem>
                            {x.divider && items[index + 1] && <Divider />}
                        </div>
                    )
                )}
            </Menu>
        </>
    )
}

export default RightClickContextMenu
